import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import {
  Plus, Trash2, Loader2, Upload, Image as ImageIcon, Receipt as ReceiptIcon,
  X, Save, Pencil, Package, BookOpen,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const fmt = (n) => {
  const num = Number(n) || 0;
  return num.toFixed(2);
};

/**
 * MaterialsPanel
 * --------------
 * Worker-facing materials editor on the Job Detail dialog (Feb 2026).
 *
 * - Lists every material entered on the job (description, qty, cost, total,
 *   optional receipt photo + who added it).
 * - Lets assignees, supervisors, and admins add/edit/remove rows.
 * - "Cost" is what the worker actually paid (their receipt). Admin applies
 *   markup later in the Invoice Editor — workers never see markup/unit_price.
 * - Receipt photo is uploaded via /api/jobs/{id}/materials/upload-receipt
 *   then the returned s3_key + url is sent with the material.
 *
 * The parent dialog hands us `canEdit` (true for admins/supervisors/assigned
 * workers). When false, the form is read-only.
 */
export default function MaterialsPanel({ job, token, canEdit, onMutated }) {
  const { toast } = useToast();
  const [materials, setMaterials] = useState(job?.materials || []);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ description: '', quantity: 1, cost: '' });
  const [draftReceipt, setDraftReceipt] = useState(null); // {url, s3_key, filename}
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [busyId, setBusyId] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  // Materials catalogue (Phase 3 — Feb 2026). Lazy-loaded on first open of
  // this panel so we don't waste a request when worker only wants to view.
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const res = await axios.get(`${API}/material-catalog`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setCatalog(res.data.items || []);
      } catch {
        // Quiet — picker just won't show if request fails.
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // When admin picks a catalogue item, pre-fill the draft. Worker can still
  // override the cost (e.g., this run they paid more than the saved cost).
  const pickFromCatalog = (id) => {
    if (!id) return;
    const item = catalog.find(c => c.id === id);
    if (!item) return;
    setDraft(d => ({
      ...d,
      description: item.description,
      // Keep whatever qty the worker already typed (default still 1).
      cost: item.default_cost != null ? String(item.default_cost) : d.cost,
    }));
  };

  // Keep local state in sync when parent reloads the job
  React.useEffect(() => {
    setMaterials(job?.materials || []);
  }, [job?.materials]);

  const totalCost = materials.reduce((s, m) => {
    const q = Number(m.quantity) || 0;
    const c = Number(m.cost != null ? m.cost : m.unit_price) || 0;
    return s + q * c;
  }, 0);

  // ─── Receipt upload helper
  const uploadReceipt = async (file, setter) => {
    setUploadingReceipt(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(
        `${API}/jobs/${job.id}/materials/upload-receipt`,
        form,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setter({ s3_key: res.data.s3_key, url: res.data.url, filename: res.data.filename });
      toast({ title: 'Receipt uploaded', description: file.name });
    } catch (err) {
      toast({
        title: 'Receipt upload failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setUploadingReceipt(false);
    }
  };

  // ─── Add a new material row
  const handleAdd = async () => {
    if (!draft.description.trim()) {
      toast({ title: 'Description required', variant: 'destructive' });
      return;
    }
    setAdding(true);
    try {
      const res = await axios.post(
        `${API}/jobs/${job.id}/materials`,
        {
          description: draft.description.trim(),
          quantity: Number(draft.quantity) || 1,
          cost: draft.cost === '' ? null : Number(draft.cost),
          // Default unit_price = cost so admin sees something. Markup applied later.
          unit_price: draft.cost === '' ? 0 : Number(draft.cost),
          receipt_url: draftReceipt?.url || null,
          receipt_s3_key: draftReceipt?.s3_key || null,
          receipt_filename: draftReceipt?.filename || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMaterials(res.data.materials || []);
      setDraft({ description: '', quantity: 1, cost: '' });
      setDraftReceipt(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onMutated?.();
      toast({ title: 'Material added' });
    } catch (err) {
      toast({
        title: 'Could not add material',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setAdding(false);
    }
  };

  // ─── Edit existing
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditDraft({
      description: m.description || '',
      quantity: m.quantity || 1,
      cost: m.cost != null ? m.cost : (m.unit_price != null ? m.unit_price : ''),
      receipt_url: m.receipt_url || null,
      receipt_s3_key: m.receipt_s3_key || null,
      receipt_filename: m.receipt_filename || null,
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = async () => {
    if (!editDraft.description?.trim()) {
      toast({ title: 'Description required', variant: 'destructive' });
      return;
    }
    setBusyId(editingId);
    try {
      const res = await axios.patch(
        `${API}/jobs/${job.id}/materials/${editingId}`,
        {
          description: editDraft.description.trim(),
          quantity: Number(editDraft.quantity) || 0,
          cost: editDraft.cost === '' || editDraft.cost == null ? null : Number(editDraft.cost),
          unit_price: editDraft.cost === '' || editDraft.cost == null ? 0 : Number(editDraft.cost),
          receipt_url: editDraft.receipt_url,
          receipt_s3_key: editDraft.receipt_s3_key,
          receipt_filename: editDraft.receipt_filename,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMaterials(res.data.materials || []);
      cancelEdit();
      onMutated?.();
      toast({ title: 'Material updated' });
    } catch (err) {
      toast({
        title: 'Could not update',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove "${m.description}"?`)) return;
    setBusyId(m.id);
    try {
      const res = await axios.delete(
        `${API}/jobs/${job.id}/materials/${m.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMaterials(res.data.materials || []);
      onMutated?.();
    } catch (err) {
      toast({
        title: 'Could not remove',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="materials-panel">
      {/* ─── Header summary */}
      <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border border-[#2D3748] bg-[#0B131F] p-3">
        <div className="flex items-center gap-2 text-sm text-slate-300">
          <Package className="w-4 h-4 text-emerald-400" />
          <span>{materials.length} material{materials.length === 1 ? '' : 's'} entered</span>
        </div>
        <div className="text-sm">
          <span className="text-slate-400 mr-1">Total cost:</span>
          <strong className="text-emerald-300" data-testid="materials-total-cost">${fmt(totalCost)}</strong>
        </div>
      </div>

      {/* ─── Existing materials list */}
      {materials.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-6 px-4 border border-dashed border-[#2D3748] rounded-md text-center">
          No materials entered yet. Add your first purchase below — admin uses these when generating the invoice.
        </div>
      ) : (
        <div className="space-y-2">
          {materials.map((m) => {
            const isEditing = editingId === m.id;
            const q = Number(m.quantity) || 0;
            const c = Number(m.cost != null ? m.cost : m.unit_price) || 0;
            const lineTotal = q * c;
            return (
              <div
                key={m.id}
                className="rounded-lg border border-[#2D3748] bg-[#0F1923] p-3"
                data-testid={`material-row-${m.id}`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <Input
                      value={editDraft.description}
                      onChange={(e) => setEditDraft(d => ({ ...d, description: e.target.value }))}
                      placeholder="Description"
                      className="bg-[#1A2433] border-[#2D3748] text-white text-sm"
                      data-testid={`material-edit-desc-${m.id}`}
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400">Qty</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editDraft.quantity}
                          onChange={(e) => setEditDraft(d => ({ ...d, quantity: e.target.value }))}
                          className="bg-[#1A2433] border-[#2D3748] text-white text-sm"
                          data-testid={`material-edit-qty-${m.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400">Cost / unit</label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editDraft.cost}
                          onChange={(e) => setEditDraft(d => ({ ...d, cost: e.target.value }))}
                          placeholder="What you paid"
                          className="bg-[#1A2433] border-[#2D3748] text-white text-sm"
                          data-testid={`material-edit-cost-${m.id}`}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-400">Total</label>
                        <div className="px-3 py-2 text-sm font-semibold text-emerald-300 bg-[#0B131F] rounded-md border border-[#2D3748]">
                          ${fmt((Number(editDraft.quantity) || 0) * (Number(editDraft.cost) || 0))}
                        </div>
                      </div>
                    </div>
                    {/* Receipt swap during edit */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {editDraft.receipt_url ? (
                        <a
                          href={editDraft.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-300 hover:underline"
                          data-testid={`material-edit-receipt-link-${m.id}`}
                        >
                          <ReceiptIcon className="w-3.5 h-3.5" /> {editDraft.receipt_filename || 'View receipt'}
                        </a>
                      ) : (
                        <span className="text-xs text-slate-500">No receipt</span>
                      )}
                      <input
                        ref={editFileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadReceipt(f, (r) => setEditDraft(d => ({
                            ...d, receipt_url: r.url, receipt_s3_key: r.s3_key, receipt_filename: r.filename,
                          })));
                        }}
                      />
                      <Button
                        type="button" size="sm" variant="outline"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={uploadingReceipt}
                        className="border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748] text-xs h-7"
                        data-testid={`material-edit-receipt-upload-${m.id}`}
                      >
                        {uploadingReceipt ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                        {editDraft.receipt_url ? 'Replace receipt' : 'Add receipt'}
                      </Button>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={saveEdit} disabled={busyId === m.id} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`material-save-${m.id}`}>
                        {busyId === m.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit} className="text-slate-400">
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-white">{m.description}</span>
                        <span className="text-xs text-slate-400">{q} × ${fmt(c)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                        {m.receipt_url && (
                          <a
                            href={m.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-300 hover:underline"
                            data-testid={`material-receipt-link-${m.id}`}
                          >
                            <ReceiptIcon className="w-3.5 h-3.5" />
                            {m.receipt_filename || 'View receipt'}
                          </a>
                        )}
                        {m.added_by_name && (
                          <span>Added by {m.added_by_name}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-semibold text-emerald-300">${fmt(lineTotal)}</div>
                      {canEdit && (
                        <div className="flex gap-1 mt-1 justify-end">
                          <button
                            type="button"
                            onClick={() => startEdit(m)}
                            disabled={busyId === m.id}
                            className="p-1 text-slate-400 hover:text-white"
                            title="Edit"
                            data-testid={`material-edit-${m.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(m)}
                            disabled={busyId === m.id}
                            className="p-1 text-rose-400 hover:text-rose-300"
                            title="Remove"
                            data-testid={`material-delete-${m.id}`}
                          >
                            {busyId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Add new material (workers + supervisors + admins) */}
      {canEdit && (
        <div className="rounded-lg border border-emerald-700/50 bg-emerald-950/20 p-3" data-testid="material-add-form">
          <div className="text-xs uppercase tracking-wider text-emerald-300 mb-2 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Add a material
          </div>
          <div className="space-y-2">
            {/* Materials Catalogue picker (Phase 3) — shows when admin has
                pre-populated commonly purchased items in Settings. */}
            {catalog.length > 0 && (
              <div data-testid="material-catalog-picker">
                <label className="text-[10px] uppercase tracking-wider text-emerald-300 flex items-center gap-1 mb-1">
                  <BookOpen className="w-3 h-3" /> Pick from catalogue (optional)
                </label>
                <select
                  defaultValue=""
                  onChange={(e) => { pickFromCatalog(e.target.value); e.target.value = ''; }}
                  className="w-full rounded-md bg-[#1A2433] border border-[#2D3748] text-white px-3 py-2 text-sm"
                  data-testid="material-catalog-select"
                  disabled={catalogLoading}
                >
                  <option value="">— Start from scratch —</option>
                  {catalog.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.description} · ${Number(c.default_cost || 0).toFixed(2)}/{c.unit_label || 'ea'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Input
              value={draft.description}
              onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
              placeholder="What did you buy? (e.g., 2x4 lumber, paint can, replacement glass)"
              className="bg-[#1A2433] border-[#2D3748] text-white text-sm"
              data-testid="material-new-desc"
            />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400">Quantity</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.quantity}
                  onChange={(e) => setDraft(d => ({ ...d, quantity: e.target.value }))}
                  placeholder="1"
                  className="bg-[#1A2433] border-[#2D3748] text-white text-sm"
                  data-testid="material-new-qty"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400">Cost / unit</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draft.cost}
                  onChange={(e) => setDraft(d => ({ ...d, cost: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1A2433] border-[#2D3748] text-white text-sm"
                  data-testid="material-new-cost"
                />
                <p className="text-[10px] text-slate-500 mt-0.5">What YOU paid (incl. tax)</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400">Total</label>
                <div className="px-3 py-2 text-sm font-semibold text-emerald-300 bg-[#0B131F] rounded-md border border-[#2D3748]">
                  ${fmt((Number(draft.quantity) || 0) * (Number(draft.cost) || 0))}
                </div>
              </div>
            </div>
            {/* Receipt photo */}
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadReceipt(f, setDraftReceipt);
                }}
                data-testid="material-new-receipt-file"
              />
              {draftReceipt ? (
                <div className="flex items-center gap-2 text-xs text-emerald-300">
                  <ImageIcon className="w-4 h-4" />
                  <span>{draftReceipt.filename}</span>
                  <button type="button" onClick={() => { setDraftReceipt(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-rose-400 hover:text-rose-300">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button" size="sm" variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingReceipt}
                  className="border-[#2D3748] bg-[#1A2433] text-white hover:bg-[#2D3748] text-xs h-7"
                  data-testid="material-new-receipt-btn"
                >
                  {uploadingReceipt ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ReceiptIcon className="w-3 h-3 mr-1" />}
                  Add receipt photo
                </Button>
              )}
              <span className="text-[10px] text-slate-500">Tap to take a photo from your phone</span>
            </div>
            <Button
              onClick={handleAdd}
              disabled={adding || !draft.description.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="material-add-btn"
            >
              {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add material
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
